import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNewCustomer } from "@/context/NewCustomerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users, Plus, Loader2, UserCircle, Building, Home, Search, ChevronLeft, ChevronRight,
  Archive, Filter, Trash2, X,
} from "lucide-react";

const TYPE_ICON = { Privato: UserCircle, Azienda: Building, Condominio: Home };
const ORIGINS = ["Passaggio spontaneo", "Cliente esistente", "Telefonata", "Segnalazione", "Social", "Google", "Campagna", "Agente", "Altro"];

export default function CustomersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const { openNew } = useNewCustomer();

  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [filters, setFilters] = useState({ search: "", type: "", office_id: "", operator_id: "", lead_origin: "", archived: "false", sort: "name", order: "asc" });
  const [page, setPage] = useState(1);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggleSel = (cid) => setSelected((s) => { const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); setFilters((s) => ({ ...s, archived: "false" })); setPage(1); };
  const selectedCustomers = data.items.filter((c) => selected.has(c.id));

  const doBulkDelete = async () => {
    setDeleting(true);
    try {
      const { data: res } = await api.post("/customers/bulk-hard-delete", { customer_ids: Array.from(selected) });
      toast.success(res.message || "Clienti eliminati definitivamente");
      exitSelect();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante l'eliminazione"); }
    finally { setDeleting(false); }
  };

  useEffect(() => {
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data)).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/customers", { params });
      setData(data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => {
    const t = setTimeout(load, filters.search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load]);

  const setF = (k, v) => { setPage(1); setFilters((s) => ({ ...s, [k]: v })); };
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="ds-page-title">Clienti</h1>
          <p className="mt-1 text-sm text-slate-500">{data.total} clienti {filters.archived === "true" ? "archiviati" : "attivi"}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            selectMode ? (
              <Button variant="outline" onClick={exitSelect} className="gap-2" data-testid="cleanup-cancel"><X className="h-4 w-4" /> Annulla selezione</Button>
            ) : (
              <Button variant="outline" onClick={() => { setFilters((s) => ({ ...s, archived: "all" })); setPage(1); setSelectMode(true); }} className="gap-2 border-danger/40 text-danger hover:bg-danger/10 hover:text-danger" data-testid="cleanup-mode-button"><Trash2 className="h-4 w-4" /> Pulisci clienti di prova</Button>
            )
          )}
          <Button onClick={openNew} className="gap-2" data-testid="customers-nuovo-cliente"><Plus className="h-4 w-4" /> Nuovo Cliente</Button>
        </div>
      </div>

      {/* Filtri */}
      <div className="ds-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca nome, CF, P.IVA, telefono, email…" value={filters.search}
              onChange={(e) => setF("search", e.target.value)} data-testid="customers-search" />
          </div>
          <Select value={filters.type || "all"} onValueChange={(v) => setF("type", v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px]" data-testid="filter-type"><SelectValue placeholder="Tipologia" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutte le tipologie</SelectItem>
              <SelectItem value="Privato">Privato</SelectItem>
              <SelectItem value="Azienda">Azienda</SelectItem>
              <SelectItem value="Condominio">Condominio</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.office_id || "all"} onValueChange={(v) => setF("office_id", v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="filter-office"><SelectValue placeholder="Sede" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutte le sedi</SelectItem>
              {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={filters.operator_id || "all"} onValueChange={(v) => setF("operator_id", v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="filter-operator"><SelectValue placeholder="Operatore" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Tutti gli operatori</SelectItem>
                {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={filters.lead_origin || "all"} onValueChange={(v) => setF("lead_origin", v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid="filter-origin"><SelectValue placeholder="Origine" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutte le origini</SelectItem>
              {ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.archived} onValueChange={(v) => setF("archived", v)}>
            <SelectTrigger className="w-[140px]" data-testid="filter-archived"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="false">Attivi</SelectItem>
              <SelectItem value="true">Archiviati</SelectItem>
              <SelectItem value="all">Tutti</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Barra selezione multipla (solo admin, modalità pulizia clienti di prova) */}
      {selectMode && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-danger/30 bg-danger/5 px-4 py-3" data-testid="cleanup-toolbar">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <Trash2 className="h-4 w-4 text-danger" />
            <span><strong data-testid="cleanup-selected-count">{selected.size}</strong> clienti selezionati per l'eliminazione definitiva</span>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={selected.size === 0 || deleting} className="gap-2 bg-danger text-white hover:bg-danger/90" data-testid="cleanup-delete-button">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Elimina definitivamente ({selected.size})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-white" data-testid="cleanup-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare definitivamente {selected.size} clienti?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div>
                    Azione <strong>irreversibile</strong>: verranno cancellati i clienti selezionati e tutti i dati collegati (forniture, opportunità, preventivi, appuntamenti, attività, storico, consensi, allegati, Coda Lavori e notifiche). Clienti selezionati:
                    <ul className="mt-2 max-h-40 list-disc overflow-auto pl-5 text-sm text-slate-700">
                      {selectedCustomers.map((c) => <li key={c.id} data-testid={`cleanup-item-${c.id}`}>{c.display_name}</li>)}
                    </ul>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cleanup-confirm-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={doBulkDelete} className="bg-danger text-white hover:bg-danger/90" data-testid="cleanup-confirm-btn">Elimina definitivamente</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Tabella */}
      <div className="ds-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-500">
            <Users className="h-8 w-8 text-slate-300" /> Nessun cliente trovato.
          </div>
        ) : (
          <div className="ds-table-scroll">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-slate-50 text-left">
                <tr className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  {selectMode && <th className="w-10 px-4 py-3"></th>}
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Tipologia</th>
                  <th className="px-4 py-3">Telefono</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Comune</th>
                  <th className="px-4 py-3">Sede</th>
                  <th className="px-4 py-3">Operatore</th>
                  <th className="px-4 py-3">Acquisizione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((c) => {
                  const Icon = TYPE_ICON[c.type] || UserCircle;
                  return (
                    <tr key={c.id} onClick={() => selectMode ? toggleSel(c.id) : navigate(`/clienti/${c.id}`)} data-testid={`customer-row-${c.id}`}
                      className={`cursor-pointer hover:bg-brand/5 ${selectMode && selected.has(c.id) ? "bg-danger/5" : ""}`}>
                      {selectMode && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSel(c.id)} data-testid={`customer-select-${c.id}`} />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 text-brand"><Icon className="h-4 w-4" /></span>
                          <span className="font-semibold text-slate-800">{c.display_name}</span>
                          {c.archived && <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500"><Archive className="h-3 w-3" /> Archiviato</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{c.type}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.phone_primary || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.comune || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.office_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.operator_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.acquisition_date ? new Date(c.acquisition_date).toLocaleDateString("it-IT") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginazione */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>Pagina {data.page} di {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /> Prec.</Button>
          <Button variant="outline" size="sm" disabled={data.page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">Succ. <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
